import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Button, useMultiStyleConfig, useBreakpointValue} from '@chakra-ui/react'
import {Link} from 'react-router-dom'

//components
import {TextTitle} from '../text-title'
import {TextTitleDescription} from '../text-title-description'
// import ResponsiveImage from '../../responsive-image'
import SocialContent from './partials/social-content'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {safeBuildUrl, getGlobalLink} from '../../../utils/contentstack-utils'
import {
    getDamImagePicture,
    getAspectRatioObjectFromImageSet,
} from '../../../custom-hooks/use-dam-image'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import SanitizedHtml from '../../html-sanitizer'
import {useProducts} from '@salesforce/commerce-sdk-react'

export const SideCard = ({
    textGroup,
    textGroupPdp,
    mobileFullSizePdp = false,
    //imageUrl,
    imageGroup, // => useDamImage(imageGroup[`image_${type}`])
    imagePosition = 'right',
    button,
    type, // standard / pdp / full
    appDownloadContent,
    backgroundColor = 'white', // white / black / grey
    hasPaddings,
    videos,
    titleAs = 'div', // if true, the title will be rendered as h2 tag
}) => {
    const [isManuallyExpanded, setIsManuallyExpanded] = useState(false)
    const descriptionRef = useRef(null)
    const {buildUrl, locale} = useMultiSite()
    const categoriesData = useGetCategoriesData()
    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: button?.productsfcc?.data?.length > 0 ? button.productsfcc.data[0].id : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'sidec-index',
            },
        },
        {
            enabled: button?.productsfcc?.data?.length > 0,
        },
    )
    const {url, label, isAbsolute} = getGlobalLink({
        link: button,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }
    const isHorizontalLayout = useBreakpointValue({base: false, lg: true}, {fallback: 'lg'})
    const isExpanded = isManuallyExpanded || isHorizontalLayout

    const isReversed = imagePosition === 'right'
    const isFull = type === 'full'
    const isPdpPage = type === 'pdp'
    const hasAppDownloads = appDownloadContent?.app_buttons?.length > 0
    const hasDetails = isPdpPage && textGroupPdp?.bodyCopy
    const styles = useMultiStyleConfig('SideCard', {
        backgroundColor,
        hasPaddings,
        isPdpPage,
        isFull,
        isReversed,
        hasDetails,
        isExpanded,
    })

    const handleToggle = () => {
        setIsManuallyExpanded(!isManuallyExpanded)
    }

    const descriptionHeight = descriptionRef.current?.clientHeight
    const textHeight = mobileFullSizePdp
        ? 'fit-content'
        : isExpanded
        ? 'auto'
        : descriptionHeight / 4
    const activeImageSet = imageGroup?.[`image_${type}`]
    const ratio = getAspectRatioObjectFromImageSet(activeImageSet)
    const altImg = ''
    return (
        <Box
            {...styles.container}
            data-testid={`${isReversed ? 'reversed' : 'non-reversed'}-container`}
            data-ab="editorial-sidecard"
            data-cs-label="editorial-sidecard"
        >
            <Flex {...styles.mainContainer}>
                <Flex
                    {...styles.outterContentContainer}
                    data-testid={`${isFull ? 'full' : 'app'}-container`}
                >
                    {/*
                  ==================================================================
                  ITEM 1: TITLE (Visible on Mobile Only)
                  This block is rendered in the DOM but hidden via CSS on desktop.
                  ==================================================================
                */}
                    <Box {...styles.mobileOnlyTitle} data-testid="mobile-only-title-container">
                        {isPdpPage ? (
                            <TextTitleDescription
                                {...styles.textTitleDescription}
                                title={textGroupPdp?.title}
                                as={titleAs}
                            />
                        ) : (
                            <>
                                {textGroup?.label && (
                                    <TextTitle {...styles.textTitleLabel}>
                                        {textGroup?.label}
                                    </TextTitle>
                                )}
                                <TextTitleDescription
                                    {...styles.textTitleDescriptionStandard}
                                    title={textGroup?.title}
                                    as={titleAs}
                                />
                            </>
                        )}
                    </Box>

                    {/*
                  ==================================================================
                  ITEM 2: MEDIA CONTAINER (Image or Video)
                  This block's code is unchanged.
                  ==================================================================
                */}
                    <Box {...styles.mediaContainer} data-testid="media-container">
                        {videos?.desktop || videos?.mobile ? (
                            <Box
                                width="100%"
                                sx={{video: {width: '100%', height: 'auto', maxHeight: '100%'}}}
                            >
                                <SideCardVideo
                                    styles={styles.video}
                                    aspectRatio={ratio}
                                    abString={'sidecard-video'}
                                    videos={videos}
                                />
                            </Box>
                        ) : (
                            activeImageSet &&
                            getDamImagePicture(activeImageSet, altImg, false, ratio)
                        )}
                    </Box>

                    {/*
                  ==================================================================
                  ITEM 3: TEXT CONTAINER (Description, CTAs, and Desktop Title)
                  ==================================================================
                */}
                    <Box
                        {...styles.textContainer}
                        data-ab="long-description"
                        data-cs-label="long-description"
                    >
                        {/* This inner Box contains the title logic and is HIDDEN on mobile */}
                        <Box {...styles.titleWrapper}>
                            {isPdpPage ? (
                                <TextTitleDescription
                                    {...styles.textTitleDescription}
                                    title={textGroupPdp?.title}
                                    as={titleAs}
                                />
                            ) : (
                                <>
                                    {textGroup?.label && (
                                        <TextTitle {...styles.textTitleLabel}>
                                            {textGroup?.label}
                                        </TextTitle>
                                    )}
                                    <TextTitleDescription
                                        {...styles.textTitleDescriptionStandard}
                                        title={textGroup?.title}
                                        as={titleAs}
                                    />
                                </>
                            )}
                        </Box>

                        {/* The rest of the content (description and buttons) is always visible */}
                        {isPdpPage ? (
                            <>
                                {hasDetails ? (
                                    <>
                                        <Box
                                            data-testid={`${
                                                mobileFullSizePdp || isExpanded
                                                    ? 'expanded'
                                                    : 'hidden'
                                            }-text`}
                                            height={textHeight}
                                            {...(mobileFullSizePdp
                                                ? styles.verticalDetailedFullSizePdp
                                                : styles.verticalDetailedPdp)}
                                        >
                                            <SanitizedHtml
                                                as={Box}
                                                html={textGroupPdp?.bodyCopy}
                                                ref={descriptionRef}
                                                data-testid="detailed-pdp"
                                                sx={styles.sanitizedHtmlPdp}
                                            />
                                        </Box>
                                        {(isExpanded
                                            ? textGroupPdp?.closeCtaTitle
                                            : textGroupPdp?.expandCtaTitle) && (
                                            <Button
                                                variant="text-button"
                                                {...styles.toggleButton}
                                                onClick={handleToggle}
                                            >
                                                {isExpanded
                                                    ? textGroupPdp.closeCtaTitle
                                                    : textGroupPdp.expandCtaTitle}
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <SanitizedHtml
                                        as={Box}
                                        html={textGroupPdp?.bodyCopy}
                                        sx={{
                                            a: {
                                                color: 'blue.500',
                                                textDecoration: 'underline',
                                                _hover: {
                                                    color: 'blue.600',
                                                },
                                            },
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <TextTitleDescription {...styles.textTitleDescriptionStandard}>
                                    <SanitizedHtml
                                        as={Box}
                                        html={textGroup?.bodyCopy}
                                        sx={{
                                            a: {
                                                color: 'blue.500',
                                                textDecoration: 'underline',
                                                _hover: {
                                                    color: 'blue.600',
                                                },
                                            },
                                        }}
                                    />
                                </TextTitleDescription>
                                {hasAppDownloads ? (
                                    <SocialContent
                                        styles={styles}
                                        appContent={appDownloadContent}
                                        {...styles.socialContent}
                                    />
                                ) : (
                                    label &&
                                    url && (
                                        <Button
                                            data-cs-label="editorial-sidecard-cta"
                                            variant={styles.buttonVariant}
                                            {...styles.braunBorderButton}
                                            {...buttonProps}
                                        >
                                            {label}
                                        </Button>
                                    )
                                )}
                            </>
                        )}
                    </Box>
                </Flex>
            </Flex>
        </Box>
    )
}

SideCard.propTypes = {
    textGroup: PropTypes.object,
    textGroupPdp: PropTypes.object,
    mobileFullSizePdp: PropTypes.bool,
    imageUrl: PropTypes.string,
    imageGroup: PropTypes.object,
    imagePosition: PropTypes.string,
    button: PropTypes.object || null,
    type: PropTypes.string,
    appDownloadContent: PropTypes.object,
    imageAlt: PropTypes.string,
    videoSrc: PropTypes.string,
    videos: PropTypes.object,
    hasDetails: PropTypes.bool,
    backgroundColor: PropTypes.string,
    hasPaddings: PropTypes.bool,
    titleAs: PropTypes.string,
}

const SideCardVideo = ({styles, aspectRatio, videos, abString = null}) => {
    return (
        <Box
            data-testid="video"
            {...styles}
            aspectRatio={aspectRatio}
            data-ab={abString || 'sidecard-video'}
            data-cs-label={abString || 'sidecard-video'}
        >
            <video playsInline autoPlay muted aria-hidden="true" loop style={styles.video}>
                <source src={videos?.desktop} type="video/mp4" media="(min-width: 1024px)" />
                <source
                    src={videos?.mobile || videos?.desktop}
                    type="video/mp4"
                    media="(max-width: 1023px)"
                />
                Your browser does not support the video tag.
            </video>
        </Box>
    )
}

SideCardVideo.propTypes = {
    styles: PropTypes.object,
    aspectRatio: PropTypes.object,
    videos: PropTypes.object,
    abString: PropTypes.string,
}
